import { clsx } from '@alexis/helpers/clsx';
import { translateText } from '@alexis/helpers/translation';
import { FlightFilterCategories } from '@pages/FlightSearchResult/components/FlightFilters/type';
import FilterButton from '@pages/FlightSearchResult/components/SortAndFilterBar/FilterButton';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Price from '@components/Price';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';
import { PAYMENT_METHOD_FILTER_BASED_ON_POS } from '@constants/flight';

import { cloudinaryImageTransformations } from '@helpers/imageTransformations';

import {
  getLocaleState,
  getTranslationsState,
  getCurrentSiteState,
  getCurrencyState,
  getPriceOptionsState,
} from '@redux/selectors';

import styles from './FilterButtons.module.scss';

type FilterButtonsProps = {
  availablePaymentMethods: HomepageFlightsPaymentMethod[];
  isDropdownOpen: boolean;
  filtersToBeDisplayed: FlightFilterCategories[];
  selectedFilterDropdown: FlightFilterCategories;
  onClearButtonClick: (filter: FlightFilterCategories) => void;
  onDropdownButtonClick: (top: number, offset: number, filter: FlightFilterCategories) => void;
};

const FilterButtons: React.FC<FilterButtonsProps> = ({
  availablePaymentMethods,
  isDropdownOpen,
  filtersToBeDisplayed,
  selectedFilterDropdown,
  onClearButtonClick,
  onDropdownButtonClick,
}) => {
  const {
    paymentMethodIdsFilter,
    stopCodesFilter,
    activeLeg,
    providersFilter,
    pricesFilter,
    airlineCodesFilter,
    allianceCodesFilter,
    appliedFilters,
  } = useFlightSearchFilters();

  const priceLabelRef = useRef<HTMLDivElement>(null);

  const [isTruncatePrice, setIsTruncatePrice] = useState(false);

  const { paymentMethods: paymentMethodsFromParams, isRoundTrip } = useFlightSearchPageParams();

  const selectedPricesForActiveLeg =
    pricesFilter.find((item) => item.leg === (isRoundTrip ? activeLeg : 0))?.values || [];

  useEffect(() => {
    if (priceLabelRef.current) {
      setIsTruncatePrice(priceLabelRef.current.offsetWidth > 170);
    }
  }, [selectedPricesForActiveLeg, priceLabelRef.current?.offsetWidth]);

  const { exchangeRate, minUsdPrice, maxUsdPrice } = useFlightSearchResults();

  const locale = useSelector(getLocaleState);
  const currency = useSelector(getCurrencyState);
  const translations = useSelector(getTranslationsState);
  const { showTotalPrice } = useSelector(getPriceOptionsState);

  const selectedStopCodesForActiveLeg =
    stopCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const currentSite = useSelector(getCurrentSiteState);

  const paymentMethods = availablePaymentMethods.filter(
    (paymentMethod) =>
      paymentMethodsFromParams.includes(paymentMethod.id) &&
      (
        PAYMENT_METHOD_FILTER_BASED_ON_POS[currentSite.countryCode] ||
        PAYMENT_METHOD_FILTER_BASED_ON_POS.default
      ).includes(paymentMethod.name.toLowerCase()),
  );

  const providersFilterForActiveLeg = useMemo(
    () => providersFilter.find((item) => item.leg === activeLeg)?.values || [],
    [providersFilter, activeLeg],
  );

  const bookingOptionsLabel = useMemo(() => {
    if (providersFilterForActiveLeg.length === 1 && providersFilterForActiveLeg[0] === 'wego') {
      return translations.book_on_wego as string;
    }

    if (providersFilterForActiveLeg.length === 1 && providersFilterForActiveLeg[0] === 'airline') {
      return translations.airline_website as string;
    }

    if (providersFilterForActiveLeg.length === 2) {
      return [translations.wego, translations.airline_website].join(', ');
    }

    return translations.booking_options as string;
  }, [
    providersFilterForActiveLeg,
    translations.airline_website,
    translations.book_on_wego,
    translations.wego,
    translations.booking_options,
  ]);

  const selectedAirlinesForActiveLeg = useMemo(
    () => airlineCodesFilter.find((item) => item.leg === activeLeg)?.values || [],
    [airlineCodesFilter, activeLeg],
  );

  const selectedAlliancesForActiveLeg = useMemo(
    () => allianceCodesFilter.find((item) => item.leg === activeLeg)?.values || [],
    [allianceCodesFilter, activeLeg],
  );

  const airlinesLabel = useMemo(() => {
    const noOfSelectedItems =
      selectedAirlinesForActiveLeg.length + selectedAlliancesForActiveLeg.length;

    const label = noOfSelectedItems <= 3 ? translations.airlines : '';
    const separator = noOfSelectedItems > 0 && noOfSelectedItems <= 3 ? ':' : '';

    const selectedAirlines = selectedAirlinesForActiveLeg.map((code) => ({
      code,
      type: 'airlines',
    }));
    const selectedAlliances = selectedAlliancesForActiveLeg.map((code) => ({
      code,
      type: 'alliances',
    }));

    return (
      <div className={styles.airlineLabel}>
        {`${label}${separator} `}
        {[...selectedAirlines, ...selectedAlliances].splice(0, 7).map((item) => (
          <img
            key={item.code}
            className={styles.image}
            src={cloudinaryImageTransformations(
              `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/${item.type}_square/${item.code}.png`,
              'c_fit',
              'w_100',
              'h_100',
              'q_auto',
              'f_auto',
              'fl_lossy',
            )}
          />
        ))}
        {noOfSelectedItems > 7 ? `+${noOfSelectedItems - 7}` : ''}
      </div>
    );
  }, [selectedAirlinesForActiveLeg, selectedAlliancesForActiveLeg]);

  const paymentMethodIdsFilterForActiveLeg = useMemo(
    () => paymentMethodIdsFilter.find((item) => item.leg === activeLeg)?.values || [],
    [paymentMethodIdsFilter, activeLeg],
  );

  return (
    <>
      {filtersToBeDisplayed.map((filter) => {
        const isCorrespondingDropdownOpen = isDropdownOpen && selectedFilterDropdown === filter;

        const handleClearIconClick = () => onClearButtonClick(filter);

        const handleDropdownButtonClick = (top: number, offset: number) =>
          onDropdownButtonClick(top, offset, filter);

        switch (filter) {
          case FlightFilterCategories.Stops:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.Stops)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {selectedStopCodesForActiveLeg.length
                  ? selectedStopCodesForActiveLeg
                      .map((id) => translateText(translations.stop_count, locale, id))
                      .join(', ')
                  : translations.stops}
              </FilterButton>
            );

          case FlightFilterCategories.PaymentMethod:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.PaymentMethod)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {paymentMethodIdsFilterForActiveLeg.length
                  ? paymentMethods
                      .filter((paymentMethod) =>
                        paymentMethodIdsFilterForActiveLeg.includes(paymentMethod.id),
                      )
                      .map((paymentMethod) => paymentMethod.name)
                      .join(', ')
                  : translations.payment_type}
              </FilterButton>
            );

          case FlightFilterCategories.BookingOptions:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.BookingOptions)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {bookingOptionsLabel}
              </FilterButton>
            );

          case FlightFilterCategories.Experience:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.Experience)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {translations.flight_experience}
              </FilterButton>
            );

          case FlightFilterCategories.Duration:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.Duration)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {translations.duration}
              </FilterButton>
            );

          case FlightFilterCategories.OriginAndDestination:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(
                  FlightFilterCategories.OriginAndDestination,
                )}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {translations.flight_origin}
              </FilterButton>
            );

          case FlightFilterCategories.Transit:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.Transit)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {translations.flight_filter_transit}
              </FilterButton>
            );

          case FlightFilterCategories.BookingSites:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.BookingSites)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {translations.booking_sites}
              </FilterButton>
            );

          case FlightFilterCategories.Times:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.Times)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {translations.flight_times}
              </FilterButton>
            );

          case FlightFilterCategories.Price:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.Price)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {!!selectedPricesForActiveLeg.length ? (
                  <>
                    <div className={styles.priceLabel} ref={priceLabelRef}>
                      <span>{showTotalPrice ? translations.total : translations.per_person}</span>
                      <div className={styles.divider}></div>

                      <div
                        className={clsx(
                          styles.priceLabelWrapper,
                          isTruncatePrice && styles.truncate,
                        )}
                      >
                        <div className={clsx(styles.priceRange)}>
                          <Price
                            isInline
                            className={styles.price}
                            price={{
                              amount: selectedPricesForActiveLeg[0] ?? minUsdPrice,
                              amountUsd: selectedPricesForActiveLeg[0] ?? minUsdPrice,
                              currencyCode: 'USD',
                            }}
                            locale={locale}
                            currency={currency}
                            exchangeRate={exchangeRate}
                          />
                          <span className={styles.priceRangeSeparator}>-</span>
                          <Price
                            isInline
                            className={styles.price}
                            price={{
                              amount: selectedPricesForActiveLeg[1] ?? maxUsdPrice,
                              amountUsd: selectedPricesForActiveLeg[1] ?? maxUsdPrice,
                              currencyCode: 'USD',
                            }}
                            locale={locale}
                            currency={currency}
                            exchangeRate={exchangeRate}
                          />
                        </div>
                      </div>

                      {isTruncatePrice ? '...' : ''}
                    </div>
                  </>
                ) : (
                  <div className={styles.priceLabel}>
                    <div>{translations.price}</div>
                    <div className={styles.divider}></div>
                    <span>{showTotalPrice ? translations.total : translations.per_person}</span>
                  </div>
                )}
              </FilterButton>
            );

          case FlightFilterCategories.Airlines:
            return (
              <FilterButton
                key={filter}
                isDropdownOpen={isCorrespondingDropdownOpen}
                isFilterApplied={appliedFilters.includes(FlightFilterCategories.Airlines)}
                onClearButtonClick={handleClearIconClick}
                onDropdownButtonClick={handleDropdownButtonClick}
              >
                {airlinesLabel}
              </FilterButton>
            );
        }
      })}
    </>
  );
};

export default FilterButtons;
